// Endpoint functions for the VAISP onboarding wallets — the rows in
// `investor_crypto_wallets` that the VAISP approve flow mints against the
// Ctrl-Alt organisation.
//
// Every `:id` here is the LOCAL wallet id, never a vendor id. That differs from
// the investor VASP registration endpoints, where approve/sync are keyed by
// Ctrl-Alt's own `vaspUserId` — a distinction worth keeping in mind when
// copying a call between the two surfaces.

import { adminApi } from '@core/api/httpClient'
import type {
  VaspWalletDetailDto,
  VaspWalletListResponseDto,
  VaspWalletStatusCountsDto,
} from './cryptoWalletContracts'
import type {
  GetVaspWalletListParams,
  VaspWalletDetails,
  VaspWalletListResponse,
  VaspWalletStatusCounts,
} from '../types/cryptoWalletTypes'
import {
  parseVaspWalletDetails,
  parseVaspWalletListResponse,
  parseVaspWalletStatusCounts,
} from './cryptoWalletParser'
import { VASP_INVESTOR_WALLETS } from './cryptoWalletsEndpointPaths'

export async function getVaspWalletList(
  params: GetVaspWalletListParams,
): Promise<VaspWalletListResponse> {
  const apiParams: Record<string, unknown> = {
    page: params.page,
    limit: params.limit,
  }
  if (params.search) apiParams.search = params.search
  // Omitted entirely when empty — that is how the "All entries" card asks for
  // every status, including any the client does not know about yet.
  if (params.walletStatus && params.walletStatus.length > 0)
    apiParams.walletStatus = params.walletStatus
  if (params.validationMethod) apiParams.validationMethod = params.validationMethod

  const { data } = await adminApi.get(VASP_INVESTOR_WALLETS, { params: apiParams })
  return parseVaspWalletListResponse((data?.data ?? data) as VaspWalletListResponseDto)
}

export async function getVaspWalletDetails(id: string): Promise<VaspWalletDetails> {
  const { data } = await adminApi.get(`${VASP_INVESTOR_WALLETS}/${id}`)
  return parseVaspWalletDetails((data?.data ?? data) as VaspWalletDetailDto)
}

export async function getVaspWalletStatusCounts(): Promise<VaspWalletStatusCounts> {
  const { data } = await adminApi.get(`${VASP_INVESTOR_WALLETS}/status-counts`)
  return parseVaspWalletStatusCounts((data?.data ?? data) as VaspWalletStatusCountsDto)
}

/**
 * Reconciles one wallet against Ctrl-Alt: admin-service reads the vendor's
 * wallets for the registration, matches this row by its `vaspReferenceId`, and
 * applies whatever status the vendor reports.
 *
 * Only meaningful for `validationMethod: 'vasp'` rows. A manual row — created by
 * the crypto-readiness toggle, with no vendor counterpart — is refused with a
 * 409, which is why the queue hides the action for those rows rather than
 * letting the operator discover it by clicking.
 *
 * Returns the reconciled wallet so the caller can show the new status without
 * waiting for the list refetch.
 */
export async function syncVaspWallet(id: string): Promise<VaspWalletDetails> {
  const { data } = await adminApi.post(`${VASP_INVESTOR_WALLETS}/${id}/sync`)
  return parseVaspWalletDetails((data?.data ?? data) as VaspWalletDetailDto)
}
